import type { ContentInput } from '../lib/types';

export class QueryGenerator {

  /**
   * Generate English-focused search queries for piracy detection
   */
  static generateEnglishQueries(title: string, year?: string, type?: string, aliases?: string[]): string[] {
    const queries: string[] = [];

    const addNoiseExclusions = (q: string) => {
      return `${q} -site:wikipedia.org -site:imdb.com -site:youtube.com -site:osnplus.com -site:osn.com -site:justwatch.com -site:rottentomatoes.com`;
    };

    const titles: string[] = [title, ...(aliases || [])].filter(Boolean);

    // English search patterns
    const baseQueries = titles.flatMap((t: string) => [
      addNoiseExclusions(`"${t}" watch online free`),
      addNoiseExclusions(`"${t}" free download`),
      addNoiseExclusions(`"${t}" stream free`),
      addNoiseExclusions(`"${t}" full ${type === 'film' ? 'movie' : 'episodes'} free`),
    ]);

    // Add year if provided
    if (year) {
      titles.forEach((t: string) => {
        baseQueries.push(addNoiseExclusions(`"${t}" ${year} watch free`));
        baseQueries.push(addNoiseExclusions(`"${t}" ${year} download`));
      });
    }

    queries.push(...baseQueries);

    // Remove duplicates
    return Array.from(new Set(queries));
  }

  /**
   * Generate Arabic-focused search queries for piracy detection
   */
  static generateArabicQueries(arabicTitle: string, year?: string, type?: string): string[] {
    const queries: string[] = [];

    const addNoiseExclusions = (q: string) => {
      return `${q} -site:wikipedia.org -site:imdb.com -site:youtube.com -site:osnplus.com -site:osn.com -site:justwatch.com -site:rottentomatoes.com`;
    };

    // Arabic search patterns (common piracy keywords in Arabic)
    const arabicQueries = [
      addNoiseExclusions(`"${arabicTitle}" مشاهدة مجانا`),           // watch free
      addNoiseExclusions(`"${arabicTitle}" تحميل`),                  // download
      addNoiseExclusions(`"${arabicTitle}" مشاهدة اون لاين`),        // watch online
      addNoiseExclusions(`"${arabicTitle}" بدون اعلانات`),           // without ads
      addNoiseExclusions(`"${arabicTitle}" مترجم`),                  // subtitled
      addNoiseExclusions(`"${arabicTitle}" تحميل مباشر`),            // direct download
    ];

    // MENA region-specific searches
    const regionalQueries = [
      addNoiseExclusions(`site:.sa OR site:.ae OR site:.eg "${arabicTitle}"`),
      addNoiseExclusions(`site:.lb OR site:.ma OR site:.qa "${arabicTitle}"`),
    ];

    queries.push(...arabicQueries, ...regionalQueries);

    // Remove duplicates
    return Array.from(new Set(queries));
  }

  /**
   * Generate search queries for piracy detection
   * Includes English and Arabic variants for MENA region
   */
  static generateQueries(content: ContentInput): string[] {
    const { title, arabicTitle, year, type, aliases } = content as any;
    const queries: string[] = [];

    // Generate English queries
    const englishQueries = this.generateEnglishQueries(title, year, type, aliases);
    queries.push(...englishQueries);

    // Generate Arabic queries if Arabic title provided
    if (arabicTitle && arabicTitle.trim()) {
      const arabicQueries = this.generateArabicQueries(arabicTitle, year, type);
      queries.push(...arabicQueries);
    } else {
      // Fallback: Generate Arabic patterns with English title (legacy behavior)
      const addNoiseExclusions = (q: string) => {
        return `${q} -site:wikipedia.org -site:imdb.com -site:youtube.com -site:osnplus.com -site:osn.com -site:justwatch.com -site:rottentomatoes.com`;
      };
      const titles: string[] = [title, ...(aliases || [])].filter(Boolean);
      const arabicQueries = titles.flatMap((t: string) => [
        addNoiseExclusions(`"${t}" مشاهدة مجانا`),
        addNoiseExclusions(`"${t}" تحميل`),
        addNoiseExclusions(`"${t}" مشاهدة اون لاين`),
        addNoiseExclusions(`"${t}" بدون اعلانات`),
        addNoiseExclusions(`"${t}" مترجم`),
        addNoiseExclusions(`"${t}" تحميل مباشر`),
      ]);
      const regionalQueries = titles.flatMap((t: string) => [
        addNoiseExclusions(`site:.sa OR site:.ae OR site:.eg "${t}" free`),
        addNoiseExclusions(`site:.lb OR site:.ma OR site:.qa "${t}" stream`),
      ]);
      queries.push(...arabicQueries, ...regionalQueries);
    }

    // Remove duplicates
    return Array.from(new Set(queries));
  }

  /**
   * Generate focused queries for high-value content
   */
  static generatePriorityQueries(content: ContentInput): string[] {
    const baseQueries = this.generateQueries(content);

    // Add additional high-signal queries for priority content
    if (content.priority === 'high') {
      // English high-priority queries
      baseQueries.push(
        `"${content.title}" torrent`,
        `"${content.title}" magnet link`,
        `"${content.title}" 1080p download`,
        `"${content.title}" HD stream free`
      );

      // Arabic high-priority queries if Arabic title provided
      if (content.arabicTitle && content.arabicTitle.trim()) {
        baseQueries.push(
          `"${content.arabicTitle}" تورنت`,           // torrent
          `"${content.arabicTitle}" 1080p`,
          `"${content.arabicTitle}" HD مجانا`          // HD free
        );
      }
    }

    return baseQueries;
  }

  /**
   * Generate queries based on content type
   */
  static generateTypeSpecificQueries(content: ContentInput): string[] {
    const { title, arabicTitle, type, aliases } = content as any;
    const queries: string[] = [];
    const titles: string[] = [title, ...(aliases || [])].filter(Boolean);

    // English type-specific queries
    switch (type) {
      case 'series':
        titles.forEach((t: string) => {
          queries.push(
            `"${t}" all episodes free`,
            `"${t}" season download`,
            `"${t}" complete series`
          );
        });
        break;

      case 'film':
        titles.forEach((t: string) => {
          queries.push(
            `"${t}" full movie free`,
            `"${t}" watch movie online`
          );
        });
        break;

      case 'original':
        titles.forEach((t: string) => {
          queries.push(
            `"${t}" OSN original free`,
            `"${t}" exclusive content`
          );
        });
        break;
    }

    // Arabic type-specific queries if Arabic title provided
    if (arabicTitle && arabicTitle.trim()) {
      switch (type) {
        case 'series':
          queries.push(
            `"${arabicTitle}" جميع الحلقات`,          // all episodes
            `"${arabicTitle}" مواسم كاملة`,            // complete seasons
            `"${arabicTitle}" حلقات مترجمة`            // subtitled episodes
          );
          break;

        case 'film':
          queries.push(
            `"${arabicTitle}" فيلم كامل`,              // full movie
            `"${arabicTitle}" فيلم مترجم`              // subtitled movie
          );
          break;

        case 'original':
          queries.push(
            `"${arabicTitle}" أصلي OSN`,               // OSN original
            `"${arabicTitle}" حصري`                    // exclusive
          );
          break;
      }
    }

    return [...this.generateQueries(content), ...queries];
  }

  /**
   * Clean and validate query
   */
  static cleanQuery(query: string): string {
    return query
      .trim()
      .replace(/\s+/g, ' ')
      .replace(/[^\w\s\u0600-\u06FF":.()-]/g, ''); // Allow Arabic characters
  }

  /**
   * Get all queries for a content item
   */
  static getAllQueries(content: ContentInput): string[] {
    let queries: string[] = [];

    // Choose query strategy based on priority
    if (content.priority === 'high') {
      queries = this.generatePriorityQueries(content);
    } else if (content.type) {
      queries = this.generateTypeSpecificQueries(content);
    } else {
      queries = this.generateQueries(content);
    }

    // Clean and deduplicate
    return Array.from(new Set(queries.map(q => this.cleanQuery(q))));
  }

  /**
   * Generate queries for visibility (neutral terms, no piracy keywords)
   * Used to measure OSN rank vs competitors using queries where legitimate services can rank
   */
  static getVisibilityQueries(content: ContentInput): string[] {
    const { title, arabicTitle, year, type, aliases } = content as any;
    const queries: string[] = [];

    // English visibility queries
    const titles: string[] = [title, ...(aliases || [])].filter(Boolean);
    for (const t of titles) {
      queries.push(
        `"${t}" watch online`,
        `"${t}" stream`,
        `"${t}" watch`,
        `"${t}" ${type === 'film' ? 'movie' : 'series'}`,
      );
      if (year) {
        queries.push(
          `"${t}" ${year} watch`,
          `"${t}" ${year} stream`
        );
      }
    }

    // Type-specific English queries
    if (type === 'series') {
      for (const t of titles) {
        queries.push(`"${t}" episodes`);
      }
    } else if (type === 'film') {
      for (const t of titles) {
        queries.push(`"${t}" movie`);
      }
    }

    // Arabic visibility queries if Arabic title provided
    if (arabicTitle && arabicTitle.trim()) {
      queries.push(
        `"${arabicTitle}" مشاهدة`,           // watch
        `"${arabicTitle}" مشاهدة اون لاين`,  // watch online
        `"${arabicTitle}" بث`,               // stream/broadcast
        `"${arabicTitle}" مترجم`,            // subtitled
      );

      // Type-specific Arabic queries
      if (type === 'series') {
        queries.push(`"${arabicTitle}" حلقات`);            // episodes
        queries.push(`"${arabicTitle}" مسلسل`);            // series
      } else if (type === 'film') {
        queries.push(`"${arabicTitle}" فيلم`);             // film
      }
    } else {
      // Fallback: Arabic patterns with English title (legacy behavior)
      for (const t of titles) {
        queries.push(
          `"${t}" مشاهدة`,
          `"${t}" مشاهدة اون لاين`,
          `"${t}" بث`,
          `"${t}" مترجم`,
        );
      }
      if (type === 'series') {
        for (const t of titles) {
          queries.push(`"${t}" حلقات`);
        }
      } else if (type === 'film') {
        for (const t of titles) {
          queries.push(`"${t}" فيلم`);
        }
      }
    }

    return Array.from(new Set(queries.map(q => this.cleanQuery(q))));
  }
}
